import { z } from 'zod'

import type { CampaignListItem, CreateCampaignRequest, UpdateCampaignRequest } from '@/api/generated'

const CAMPAIGN_TYPES = ['sponsor', 'affiliate', 'partnership', 'community', 'internal'] as const
const CAMPAIGN_STATUSES = ['active', 'paused', 'ended', 'archived'] as const

export const campaignFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  campaignType: z.enum(CAMPAIGN_TYPES).optional(),
  status: z.enum(CAMPAIGN_STATUSES).optional(),
  trialDays: z.coerce.number<number>().min(1, 'Must be at least 1').max(365, 'Max 365 days'),
  sourceName: z.string().min(1, 'Source name is required'),
  attributionMessage: z.string().optional(),
  maxRedemptions: z.string().optional(),
  startsAt: z.string().optional(),
  expiresAt: z.string().optional(),
  description: z.string().optional(),
})

export type CampaignFormValues = z.infer<typeof campaignFormSchema>

export const defaultFormValues: CampaignFormValues = {
  name: '',
  trialDays: 30,
  sourceName: '',
  attributionMessage: '',
  maxRedemptions: '',
  startsAt: '',
  expiresAt: '',
  description: '',
}

const extractDateString = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ''
  try {
    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''
    return date.toISOString().split('T')[0]
  } catch {
    return ''
  }
}

export function campaignToFormValues(campaign: CampaignListItem): CampaignFormValues {
  return {
    name: campaign.name,
    campaignType: campaign.campaign_type as CampaignFormValues['campaignType'],
    status: campaign.status as CampaignFormValues['status'],
    trialDays: campaign.trial_days,
    sourceName: campaign.source_name,
    attributionMessage: campaign.attribution_message ?? '',
    maxRedemptions: campaign.max_redemptions?.toString() ?? '',
    startsAt: extractDateString(campaign.starts_at),
    expiresAt: extractDateString(campaign.expires_at),
    description: campaign.description ?? '',
  }
}

export function formValuesToCreatePayload(values: CampaignFormValues): CreateCampaignRequest {
  return {
    name: values.name,
    campaign_type: values.campaignType || undefined,
    trial_days: values.trialDays,
    source_name: values.sourceName,
    attribution_message: values.attributionMessage || undefined,
    max_redemptions: values.maxRedemptions ? Number(values.maxRedemptions) : undefined,
    starts_at: values.startsAt || undefined,
    expires_at: values.expiresAt || undefined,
    description: values.description || undefined,
  }
}

export function formValuesToUpdatePayload(values: CampaignFormValues): UpdateCampaignRequest {
  return {
    name: values.name,
    campaign_type: values.campaignType,
    status: values.status,
    trial_days: values.trialDays,
    source_name: values.sourceName,
    attribution_message: values.attributionMessage || null,
    max_redemptions: values.maxRedemptions ? Number(values.maxRedemptions) : null,
    starts_at: values.startsAt || null,
    expires_at: values.expiresAt || null,
    description: values.description || null,
  }
}

export { CAMPAIGN_STATUSES, CAMPAIGN_TYPES }
